import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Paper,
  Grid,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import MedicationIcon from '@mui/icons-material/Medication';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import HomeIcon from '@mui/icons-material/Home';
import LoginIcon from '@mui/icons-material/Login';
import { SelectChangeEvent } from '@mui/material/Select';

interface FormData {
  [key: string]: string;  // Add index signature
  name: string;
  municipality: string;
  province: string;
  age: string;
  sex: string;
  civilStatus: string;
  philHealthNumber: string;
  phoneNumber: string;
  password: string;
  confirmPassword: string;
}

// Define a type for the philippineLocations object
type PhilippineLocations = {
  [key: string]: string[];
};

// Philippine provinces and municipalities data
const philippineLocations: PhilippineLocations = {
  "Metro Manila": [
    "Caloocan", "Las Piñas", "Makati", "Malabon", "Mandaluyong", "Manila", 
    "Marikina", "Muntinlupa", "Navotas", "Parañaque", "Pasay", "Pasig", 
    "Pateros", "Quezon City", "San Juan", "Taguig", "Valenzuela"
  ],
  "Cebu": [
    "Cebu City", "Mandaue", "Lapu-Lapu", "Danao", "Talisay", "Carcar", "Naga",
    "Bogo", "Toledo", "Bantayan", "Balamban", "Alcantara", "Alcoy", "Alegria"
  ],
  "Davao": [
    "Davao City", "Digos", "Mati", "Panabo", "Samal", "Tagum", "Malita",
    "Padada", "Santa Cruz", "Bansalan", "Hagonoy", "Matanao", "Don Marcelino"
  ],
  "Cavite": [
    "Cavite City", "Tagaytay", "Trece Martires", "Dasmariñas", "Bacoor", 
    "Imus", "General Trias", "Tanza", "Silang", "Carmona", "Maragondon"
  ],
  "Batangas": [
    "Batangas City", "Lipa", "Tanauan", "Santo Tomas", "Nasugbu", "Bauan",
    "Lemery", "Taal", "San Juan", "Rosario", "Malvar", "Mabini", "Lobo"
  ],
  "Laguna": [
    "Santa Rosa", "Calamba", "San Pablo", "Biñan", "Cabuyao", "Los Baños",
    "San Pedro", "Pagsanjan", "Pila", "Mabitac", "Nagcarlan", "Lumban"
  ],
  "Iloilo": [
    "Iloilo City", "Passi", "Oton", "Pavia", "Santa Barbara", "Dumangas",
    "Leganes", "New Lucena", "Zarraga", "Alimodian", "Leon", "Tigbauan"
  ],
  "Pampanga": [
    "Angeles", "San Fernando", "Mabalacat", "Apalit", "Bacolor", "Floridablanca",
    "Guagua", "Lubao", "Macabebe", "Mexico", "Porac", "Santa Rita"
  ],
  "Rizal": [
    "Antipolo", "Cainta", "Taytay", "Angono", "Binangonan", "Rodriguez",
    "San Mateo", "Tanay", "Teresa", "Morong", "Pililla", "Baras"
  ],
  "Bulacan": [
    "Malolos", "Meycauayan", "San Jose del Monte", "Baliuag", "Plaridel",
    "Pulilan", "Calumpit", "Hagonoy", "Obando", "Paombong", "Bocaue"
  ]
  // Data for other provinces would follow the same pattern
};

const provincesList = Object.keys(philippineLocations);

const Signup: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>({
    name: '',
    municipality: '',
    province: '',
    age: '',
    sex: '',
    civilStatus: '',
    philHealthNumber: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const [municipalities, setMunicipalities] = useState<string[]>([]);

  // Update municipalities when province changes
  useEffect(() => {
    if (formData.province && philippineLocations[formData.province]) {
      setMunicipalities(philippineLocations[formData.province]);
    } else {
      setMunicipalities([]);
    }
  }, [formData.province]);

  // For TextField components
  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // For Select components
  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (name === 'province') {
      setFormData(prev => ({ ...prev, municipality: '' }));
    }
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    // Required fields validation
    const requiredFields = ['name', 'municipality', 'province', 'age', 'sex', 'civilStatus', 'phoneNumber', 'password', 'confirmPassword'];
    requiredFields.forEach(field => {
      if (!formData[field]) {
        newErrors[field] = 'This field is required';
      }
    });

    // Phone number validation
    if (formData.phoneNumber && !/^09\d{9}$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = 'Please enter a valid Philippine mobile number (e.g., 09123456789)';
    }

    // Age validation
    if (formData.age && (parseInt(formData.age) < 18 || parseInt(formData.age) > 100)) {
      newErrors.age = 'Age must be between 18 and 100';
    }

    // Password validation
    if (formData.password && formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    }

    // Confirm password validation
    if (formData.confirmPassword && formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      navigate('/signup-success');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ overflow: 'hidden' }}>
        <Grid container>
          {/* Form Column */}
          <Grid item xs={12} md={7} sx={{ p: 4 }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h4" gutterBottom color="primary">
                Sign Up
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Create your account to start your TB treatment journey
              </Typography>
            </Box>

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    name="name"
                    value={formData.name}
                    onChange={handleTextChange}
                    error={!!errors.name}
                    helperText={errors.name}
                    required
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={!!errors.province} required>
                    <InputLabel>Province</InputLabel>
                    <Select
                      name="province"
                      value={formData.province}
                      label="Province"
                      onChange={handleSelectChange}
                    >
                      <MenuItem value="">
                        <em>Select a province</em>
                      </MenuItem>
                      {provincesList.map((province) => (
                        <MenuItem key={province} value={province}>
                          {province}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.province && <FormHelperText>{errors.province}</FormHelperText>}
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={!!errors.municipality} required disabled={!formData.province}>
                    <InputLabel>Municipality/City</InputLabel>
                    <Select
                      name="municipality"
                      value={formData.municipality}
                      label="Municipality/City"
                      onChange={handleSelectChange}
                    >
                      <MenuItem value="">
                        <em>Select a municipality/city</em>
                      </MenuItem>
                      {municipalities.map((municipality) => (
                        <MenuItem key={municipality} value={municipality}>
                          {municipality}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.municipality && <FormHelperText>{errors.municipality}</FormHelperText>}
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Age"
                    name="age"
                    type="number"
                    value={formData.age}
                    onChange={handleTextChange}
                    error={!!errors.age}
                    helperText={errors.age}
                    required
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    select
                    label="Sex"
                    name="sex"
                    value={formData.sex}
                    onChange={handleTextChange}
                    error={!!errors.sex}
                    helperText={errors.sex}
                    required
                  >
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    select
                    label="Civil Status"
                    name="civilStatus"
                    value={formData.civilStatus}
                    onChange={handleTextChange}
                    error={!!errors.civilStatus}
                    helperText={errors.civilStatus}
                    required
                  >
                    <MenuItem value="Single">Single</MenuItem>
                    <MenuItem value="Married">Married</MenuItem>
                    <MenuItem value="Widowed">Widowed</MenuItem>
                    <MenuItem value="Divorced">Divorced</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="PhilHealth Number"
                    name="philHealthNumber"
                    value={formData.philHealthNumber}
                    onChange={handleTextChange}
                    helperText="Optional"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleTextChange}
                    error={!!errors.phoneNumber}
                    helperText={errors.phoneNumber || "Format: 09123456789"}
                    required
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleTextChange}
                    error={!!errors.password}
                    helperText={errors.password}
                    required
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Confirm Password"
                    name="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={handleTextChange}
                    error={!!errors.confirmPassword}
                    helperText={errors.confirmPassword}
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                    fullWidth
                    sx={{ mt: 1 }}
                  >
                    Sign Up
                  </Button>
                  
                  <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 3 }}>
                    <Button
                      startIcon={<LoginIcon />}
                      onClick={() => navigate('/login')}
                    >
                      Login Instead
                    </Button>
                    <Button
                      startIcon={<HomeIcon />}
                      onClick={() => navigate('/')}
                    >
                      Back to Home
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </Grid>

          {/* Info Column */}
          <Grid item xs={12} md={5} sx={{ 
            bgcolor: 'primary.main', 
            color: 'white',
            p: 4,
            display: 'flex',
            flexDirection: 'column'
          }}>
            <Box sx={{ mb: 4 }}>
              <Typography variant="h4" gutterBottom>
                TB Treatment Adherence Monitoring System
              </Typography>
              <Typography variant="body1">
                Join our platform to receive support and reminders throughout your TB treatment journey
              </Typography>
            </Box>

            <Divider sx={{ bgcolor: 'rgba(255,255,255,0.2)', my: 2 }} />

            <List sx={{ mt: 2 }}>
              <ListItem>
                <ListItemIcon>
                  <NotificationsActiveIcon sx={{ color: 'white' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Daily Medication Reminders" 
                  secondary="Receive timely SMS reminders to take your medication" 
                  sx={{ 
                    '& .MuiListItemText-secondary': { 
                      color: 'rgba(255,255,255,0.7)' 
                    } 
                  }}
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <SupportAgentIcon sx={{ color: 'white' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Caregiver Support" 
                  secondary="Connect with your treatment supporter for better adherence"
                  sx={{ 
                    '& .MuiListItemText-secondary': { 
                      color: 'rgba(255,255,255,0.7)' 
                    } 
                  }}
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <MedicationIcon sx={{ color: 'white' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Medication Tracking" 
                  secondary="Track your medication history and adherence progress"
                  sx={{ 
                    '& .MuiListItemText-secondary': { 
                      color: 'rgba(255,255,255,0.7)' 
                    } 
                  }}
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <HealthAndSafetyIcon sx={{ color: 'white' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Health Monitoring" 
                  secondary="Monitor your treatment progress with healthcare professionals"
                  sx={{ 
                    '& .MuiListItemText-secondary': { 
                      color: 'rgba(255,255,255,0.7)' 
                    } 
                  }}
                />
              </ListItem>
            </List>

            <Box sx={{ mt: 'auto', pt: 4 }}>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                By joining our system, you'll be taking an important step towards successful TB treatment completion. Our platform is designed to support you every step of the way.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default Signup; 